import { resolveTimezoneId } from '../resolveTimezoneId.js';

/**
 * Pure mappers between the React `calendarMonthSlice.filters` shape and the
 * Symfony saved-filter `filter` JSON blob (sub-epic 9.4).
 *
 * The backend stores `filter` as `json_decode` with no schema (see
 * `UserCalendarFilter.php:32`), so the Phase 1 contract is owned entirely
 * by the React layer:
 *   - booleans go as `0`/`1` on the wire (legacy parity — the AngularJS
 *     impl used the same convention; `fromApiFilter` tolerates both formats)
 *   - multi-selects go as numeric ID arrays (`locationID`, `resourceID`, …)
 *   - `view` key (`'month'|'week'|'day'`) scopes the filter to one view
 *     so the same `/user/filter` table can back Month, Week, and Day views
 *   - `organizationID` is a **lookup** list; callers pass `organizationMap`
 *     so we can resolve `filters.org` (unique string) to the numeric id
 */

/**
 * Convert slice filter state → API JSON blob.
 *
 * @param {Object} filters slice `filters` state
 * @param {string} view  'month' | 'week' | 'day'
 * @param {Object<string, Object>} [organizationMap={}] legacy
 *   `state.organizations` map keyed by org id
 * @returns {Object}
 */
export function toApiFilter(filters = {}, view, organizationMap = {}) {
	// Sub-epic 10.2: `filters.org` is now `string[]`. Still tolerant of the
	// legacy scalar shape in case a stale saved filter round-trips through
	// a shorter-lived browser session.
	const orgArr = Array.isArray(filters.org)
		? filters.org.filter(v => v != null && v !== '')
		: filters.org
			? [filters.org]
			: [];
	const orgIds = orgArr.flatMap(u => resolveOrgId(u, organizationMap));

	return {
		view: view || null,
		// `_action` lets the LEGACY global-nav menu build a valid deep-link
		// (`/calendar/<_action>/<date>/…`); it keys its date branch off `_action`
		// too. Mirrors `view` (BUG-SavedFilterLink).
		_action: view || null,
		// Saved filters are bound to a SINGLE org (product fact), and the legacy
		// menu prints `/org/{{ organizationUnique }}/` — an array would render the
		// literal string "Array" → `/org/Array/` 404. Store the single slug
		// (string) or null. `organizationID` stays an array (its `|join` is fine).
		organizationUnique: orgArr[0] || null,
		organizationID: orgIds,
		locationID: toNumberArray(filters.location),
		resourceID: toNumberArray(filters.resource),
		pilotID: toNumberArray(filters.pilot),
		instructorID: toNumberArray(filters.instructor),
		maintenance: toLegacyBool(filters.maintenance),
		waiting: toLegacyBool(filters.waiting),
		myonly: toLegacyBool(filters.myonly),
		activityTypeColorScheme: toLegacyBool(filters.activityTypeColorScheme),
		timezone: resolveTimezoneId(filters.timezone, 'UTC')
	};
}

/**
 * Convert API JSON blob → slice filter partial. Intentionally returns a
 * partial update (no `searchQuery`) so `setFilters({...fromApiFilter(api)})`
 * leaves the user's local search text intact.
 *
 * Tolerant of legacy shapes: booleans as `1`/`0`/`"1"`/`"0"` all coerce.
 *
 * @param {Object} apiFilter
 * @returns {Object} slice partial
 */
export function fromApiFilter(apiFilter = {}) {
	// Read the org array; tolerate scalar legacy shape from pre-10.2 blobs.
	const rawOrg = apiFilter.organizationUnique;
	const orgArr = Array.isArray(rawOrg)
		? rawOrg.filter(v => v != null && v !== '').map(String)
		: rawOrg
			? [String(rawOrg)]
			: [];
	return {
		org: orgArr,
		location: toStringArray(apiFilter.locationID),
		resource: toStringArray(apiFilter.resourceID),
		pilot: toStringArray(apiFilter.pilotID),
		instructor: toStringArray(apiFilter.instructorID),
		timezone: resolveTimezoneId(apiFilter.timezone, 'UTC'),
		maintenance: fromLegacyBool(apiFilter.maintenance, true),
		myonly: fromLegacyBool(apiFilter.myonly, false),
		waiting: fromLegacyBool(apiFilter.waiting, true),
		activityTypeColorScheme: fromLegacyBool(apiFilter.activityTypeColorScheme, false)
	};
}

// ── Helpers ────────────────────────────────────────────────────────────────

function toNumberArray(source) {
	if (!Array.isArray(source)) return [];
	return source
		.filter(v => v != null && v !== '')
		.map(v => Number(v))
		.filter(n => Number.isFinite(n));
}

function toStringArray(source) {
	if (!Array.isArray(source)) return [];
	return source
		.filter(v => v != null && v !== '')
		.map(String);
}

function toLegacyBool(value) {
	return value ? 1 : 0;
}

/**
 * Tolerant boolean reader: accepts `true`/`false`, `1`/`0`, `"1"`/`"0"`,
 * and falls back to the provided default when the key is absent.
 */
function fromLegacyBool(value, defaultValue) {
	if (value === undefined || value === null) return defaultValue;
	if (typeof value === 'boolean') return value;
	if (value === 1 || value === '1') return true;
	if (value === 0 || value === '0') return false;
	return !!value;
}

/**
 * Resolve an organization unique slug to a numeric id by scanning the
 * legacy organizations map. Returns `[id]` on hit, `[]` on miss.
 */
function resolveOrgId(orgUnique, organizationMap) {
	if (!orgUnique || !organizationMap) return [];
	const values = Array.isArray(organizationMap)
		? organizationMap
		: Object.values(organizationMap);
	for (let i = 0; i < values.length; i += 1) {
		const o = values[i];
		if (!o) continue;
		if ((o.organizationUnique || o.unique) === orgUnique) {
			const id = o.organizationID ?? o.id;
			if (id != null) return [Number(id)];
		}
	}
	return [];
}
